import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { X, Download, ChevronDown, Search, Loader2 } from "lucide-react";
import { searchPatientsFull, mapPgPatient, mapRosterPatient, rosterAddPatient, manualPayers } from "@/lib/noteClaim";

const HAIR = "rgba(0,0,0,.1)", INK = "#141412", GREY = "#6E6E6B";

function Shell({ title, onClose, children, testid, wide, closeTestid }) {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9800, background: "rgba(20,20,18,.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={onClose} data-testid={testid}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: wide ? "min(600px,96vw)" : "min(460px,96vw)", maxHeight: "86vh", background: "#fff", borderRadius: 16, display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 24px 70px rgba(0,0,0,.3)" }}>
        <div className="flex items-center gap-2 px-5 py-3 border-b" style={{ borderColor: HAIR }}>
          <div className="text-[15px] font-bold" style={{ color: INK }}>{title}</div>
          <span className="flex-1" />
          <button onClick={onClose} aria-label="Close" style={{ color: GREY }} data-testid={closeTestid}><X size={18} /></button>
        </div>
        <div className="overflow-auto" style={{ padding: 18, background: "#F7F7F9" }}>{children}</div>
      </div>
    </div>
  );
}

const sentence = (v) => { const t = String(v).replace(/_/g, " ").toLowerCase(); return t.charAt(0).toUpperCase() + t.slice(1); };
const detailOf = (c) => [c.field_label ? sentence(c.field_label) : c.category && String(c.category).replace(/_/g, " ").replace(/^./, (x) => x.toUpperCase()), (c.lines_added || c.lines_removed) ? `+${c.lines_added || 0} −${c.lines_removed || 0} lines` : "", c.ip ? `IP ${c.ip}` : ""].filter(Boolean).join(" · ");
// API diff → [{ t: "+"|"-"|" ", s }]. Accepts a unified-diff string or an array of lines/objects.
function diffLines(c) {
  const d = c.diff ?? c.line_diff ?? c.changes;
  const arr = typeof d === "string" ? d.split("\n") : Array.isArray(d) ? d : [];
  return arr.map((x) => {
    if (typeof x === "string") { const t = x[0] === "+" || x[0] === "-" ? x[0] : " "; return { t, s: t === " " ? x : x.slice(1).trimStart() }; }
    if (x.t === "hunk") return null;
    const t = x.t === "add" || x.t === "+" || x.op === "add" ? "+" : x.t === "del" || x.t === "-" || x.op === "remove" ? "-" : " ";
    return { t, s: x.text ?? x.line ?? x.s ?? "" };
  }).filter((x) => x && !(x.t === " " && x.s === "") && !/^(\+\+\+|---|@@)/.test(x.s));
}

const whenOf = (iso) => { if (!iso) return ""; const d = new Date(iso); return isNaN(d) ? String(iso).replace("T", " ").slice(0, 16) : d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); };

// Unsign from the claim top bar — always asks first.
export function UnsignConfirm({ rect, onCancel, onConfirm }) {
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) onCancel(); };
    const onKey = (e) => { if (e.key === "Escape") onCancel(); };
    document.addEventListener("mousedown", onDoc); document.addEventListener("keydown", onKey);
    ref.current?.querySelector("[data-no]")?.focus();
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [onCancel]);
  const W = 320, left = Math.max(8, Math.min(rect.left, window.innerWidth - W - 8));
  return createPortal(
    <div ref={ref} role="dialog" className="fixed bg-white rounded-[12px]" style={{ left, top: rect.bottom + 6, zIndex: 9700, width: W, padding: "14px 16px", border: `1px solid ${HAIR}`, boxShadow: "0 16px 44px rgba(0,0,0,.16)" }} data-testid="ws-unsign-confirm">
      <b className="block text-[14px] mb-1" style={{ color: INK }}>Unsign this visit?</b>
      <p className="text-[13px] mb-3" style={{ color: "#6C6C69" }}>Claim saves are blocked until it's signed again. This is recorded in the change log.</p>
      <div className="flex justify-end gap-2">
        <button data-no onClick={onCancel} className="text-[13px] font-semibold rounded-[8px] px-3 h-[32px] bg-white" style={{ border: `1px solid ${HAIR}`, color: INK }} data-testid="ws-unsign-keep">Keep signed</button>
        <button onClick={onConfirm} className="text-[13px] font-semibold rounded-[8px] px-3 h-[32px] text-white" style={{ background: INK }} data-testid="ws-unsign-yes">Unsign</button>
      </div>
    </div>, document.body);
}

// §3.6 — visit change log (who/when/IP + line diff) with CSV export.
const CL_FILTERS = [["all", "All"], ["note", "Note"], ["claim", "Claim"]];
const clCat = (c) => (String(c.category || "").toLowerCase() === "claim" ? "claim" : "note");
export function ChangeLogModal({ data, visitId, onCsv, onClose, filter = "all", onFilter }) {
  const all = Array.isArray(data) ? data : [];
  const rows = filter === "all" ? all : all.filter((c) => clCat(c) === filter);
  return (
    <Shell title="Change log" onClose={onClose} testid="ws-changelog-modal" wide>
      <div className="text-[13px] mb-2" style={{ color: "#6C6C69" }} data-testid="ws-changelog-intro">Every change to this visit, newest first. It can't be edited or deleted.</div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[12.5px]" style={{ color: GREY }}>{visitId ? `Visit ${visitId}` : ""}{Array.isArray(data) ? ` · ${rows.length} ${rows.length === 1 ? "entry" : "entries"}` : ""}</div>
        <span className="flex-1" />
        {onFilter ? (
          <span className="inline-flex p-[2px] rounded-[8px] mr-2" style={{ background: "#F1F1F4" }} data-testid="ws-changelog-filter">
            {CL_FILTERS.map(([k, l]) => (
              <button key={k} type="button" onClick={() => onFilter(k)} aria-pressed={filter === k} data-testid={`ws-changelog-filter-${k}`}
                className="text-[12px] font-semibold rounded-[6px] px-2.5 h-[24px]" style={filter === k ? { background: "#fff", color: INK, boxShadow: "0 1px 2px rgba(20,20,18,.12)" } : { color: "#6E6E6B" }}>{l}</button>
            ))}
          </span>
        ) : null}
        <button onClick={onCsv} disabled={!rows.length} className="text-[12px] font-semibold rounded-[8px] px-3 h-[30px] border bg-white inline-flex items-center gap-1.5 disabled:opacity-40" style={{ borderColor: HAIR, color: INK }} data-testid="ws-changelog-csv"><Download size={13} />Export CSV</button>
      </div>
      {data === "loading" ? (
        <div className="text-[13px] py-10 text-center" style={{ color: GREY }}>Loading…</div>
      ) : !rows.length ? (
        <div className="text-[13px] py-10 text-center" style={{ color: GREY }} data-testid="ws-changelog-empty">{filter === "all" ? "No changes recorded for this visit yet." : `No ${filter} changes recorded for this visit yet.`}</div>
      ) : (
        <ol className="flex flex-col gap-2" data-testid="ws-changelog-list">
          {rows.map((c, i) => (
            <li key={i} className="bg-white rounded-[11px] border p-3" style={{ borderColor: "#E9E9EC" }} data-testid={`ws-changelog-row-${i}`}>
              <div className="flex items-baseline gap-2">
                <span className="text-[13px] font-semibold" style={{ color: INK }}>{c.label || c.action || "Change"}</span>
                <span className="flex-1" />
                <span className="text-[11.5px] tabular-nums" style={{ color: GREY }}>{(c.user_name || c.user_email || "System") + (c.is_sub ? " (sub)" : "")} · {whenOf(c.at || c.time)}</span>
              </div>
              {detailOf(c) ? <div className="text-[12px] mt-0.5" style={{ color: "#6E6E6B" }}>{detailOf(c)}</div> : null}
              {diffLines(c).length ? (
                <div className="mt-2 rounded-[8px] p-2 overflow-x-auto" style={{ border: "1px solid #E9E9EC", fontFamily: "ui-monospace,Menlo,monospace", fontSize: 12, lineHeight: 1.5 }} data-testid={`ws-changelog-diff-${i}`}>
                  {diffLines(c).map((d, k) => (
                    <div key={k} style={d.t === "-" ? { color: "#9B9B97", textDecoration: "line-through" } : { color: d.t === "+" ? INK : "#6E6E6B" }}>{d.t === " " ? "  " : `${d.t} `}{d.s}</div>
                  ))}
                </div>
              ) : null}
            </li>
          ))}
        </ol>
      )}
    </Shell>
  );
}

// §3.7 — Match patient. Match existing (search-only) + Add new patient (full demographics + payer).
const SRC_LABEL = { postgres: "Live chart", roster: "Roster", data_migration: "Migrated", synthetic: "Demo" };
const linkErrMsg = (e) => (e?.response?.status === 404 ? "That patient could not be found." : e?.response?.data?.detail?.message || e?.response?.data?.detail || "Couldn't link that patient.");

function PayerPicker({ clientId, value, onPick }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const INP = "w-full text-[13px] px-3 py-2 rounded-[9px] outline-none bg-white";
  useEffect(() => {
    if (!open) return undefined;
    setLoading(true);
    const t = setTimeout(async () => { try { setItems(await manualPayers(clientId, q)); } catch { setItems([]); } finally { setLoading(false); } }, 250);
    return () => clearTimeout(t);
  }, [q, open, clientId]);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc); return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  return (
    <div className="relative" ref={ref} data-testid="pm-add-payer">
      <button type="button" onClick={() => setOpen((o) => !o)} className={INP + " text-left flex items-center justify-between"} style={{ border: `1px solid ${HAIR}` }} data-testid="pm-add-payer-trigger">
        <span className="truncate" style={{ color: value ? INK : GREY }}>{value || "Search payer…"}</span>
        <ChevronDown size={14} style={{ color: GREY, flex: "none" }} />
      </button>
      {open ? (
        <div className="absolute z-[60] left-0 right-0 mt-1 bg-white rounded-[10px] p-1.5 max-h-[220px] overflow-auto" style={{ border: `1px solid ${HAIR}`, boxShadow: "0 16px 44px rgba(0,0,0,.16)" }}>
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Payer name or id" className="w-full text-[13px] px-2.5 py-1.5 rounded-[7px] outline-none mb-1 sticky top-0" style={{ border: `1px solid ${HAIR}` }} />
          {loading ? <div className="text-[12px] px-2 py-2 inline-flex items-center gap-1.5" style={{ color: GREY }}><Loader2 size={12} className="animate-spin" />Searching…</div>
            : !items.length ? <div className="text-[12px] px-2 py-2" style={{ color: GREY }}>{q.trim() ? "No payers match." : "Type to search this client's payers."}</div>
            : items.map((it, i) => (
              <button key={i} type="button" onClick={() => { onPick(it); setOpen(false); }} className="w-full text-left text-[12.5px] px-2 py-1.5 rounded-[7px] hover:bg-[#F1F1F4]" data-testid={`pm-add-payer-opt-${i}`} style={{ color: INK }}>
                {it.payer_name}{it.payer_id ? <span style={{ color: GREY }}> · {it.payer_id}</span> : ""}
              </button>
            ))}
        </div>
      ) : null}
    </div>
  );
}

const Field = ({ label, k, f, setF, type = "text", testid, placeholder }) => (
  <div>
    <label className="text-[11px] font-bold uppercase" style={{ color: GREY, letterSpacing: ".05em" }}>{label}</label>
    <input type={type} value={f[k]} placeholder={placeholder} onChange={(e) => setF((s) => ({ ...s, [k]: e.target.value }))} className="w-full text-[13px] px-3 py-2 rounded-[9px] outline-none bg-white mt-1" style={{ border: `1px solid ${HAIR}` }} data-testid={testid} />
  </div>
);

export function MatchPatientModal({ note, onDone, onClose }) {
  const noteId = note?.id;
  const clientId = note?.client_id;
  const [tab, setTab] = useState("match"); // match | add
  const [busy, setBusy] = useState(false);
  // Match (search-only)
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [src, setSrc] = useState("");
  const [bound, setBound] = useState(true);
  const [searching, setSearching] = useState(false);
  const [linkingMrn, setLinkingMrn] = useState("");
  // Add new patient
  const [f, setF] = useState({ first_name: "", last_name: "", dob: "", gender: "", phone: "", email: "", street: "", city: "", state: "", zip: "", payer_name: "", payer_id: "", member_id: "", mrn: "" });
  const [dups, setDups] = useState(null);

  // Debounced, min-2-char search; shows nothing until then.
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setRows([]); setSearching(false); return undefined; }
    setSearching(true);
    const t = setTimeout(async () => {
      const res = await searchPatientsFull(clientId, term);
      setRows(res.results); setSrc(res.source); setBound(res.bound); setSearching(false);
    }, 300);
    return () => clearTimeout(t);
  }, [q, clientId]);

  const done = () => { onDone?.(); onClose(); };

  const pick = async (r) => {
    const rowSrc = r.source || src;
    setBusy(true); setLinkingMrn(r.mrn || r.full_name || "x");
    try {
      if (rowSrc === "postgres") await mapPgPatient(noteId, r.mrn);
      else if (rowSrc === "roster") await mapRosterPatient(noteId, r.mrn);
      else {
        const fn = (r.full_name || "").split(" ");
        const res = await rosterAddPatient(noteId, {
          first_name: r.first_name || fn[0] || "", last_name: r.last_name || fn.slice(1).join(" ") || "",
          dob: r.dob, gender: r.gender || "", mrn: r.mrn || "",
          member_id: r.insurance?.member_id || "", payer_name: r.insurance?.payer_name || "", force: true,
        });
        if (!res.ok) { toast.error("Couldn't link this patient."); return; }
      }
      toast.success(`Linked ${r.full_name || r.mrn}`);
      done();
    } catch (e) { toast.error(linkErrMsg(e)); }
    finally { setBusy(false); setLinkingMrn(""); }
  };

  const save = async (force = false) => {
    if (!f.first_name.trim() || !f.last_name.trim() || !f.dob) { toast.error("First name, last name and date of birth are required."); return; }
    setBusy(true); if (!force) setDups(null);
    try {
      const res = await rosterAddPatient(noteId, { ...f, state: (f.state || "").toUpperCase().slice(0, 2), force });
      if (res.ok) { toast.success(`Added to roster as MRN ${res.data?.mrn || ""} and linked`.trim()); done(); }
      else { setDups(res.duplicates || []); toast.message("A matching patient already exists — link it instead."); }
    } catch (e) { toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Could not add patient."); }
    finally { setBusy(false); }
  };

  const linkDup = async (mrn) => {
    setBusy(true);
    try { await mapRosterPatient(noteId, mrn); toast.success(`Linked ${mrn}`); done(); }
    catch (e) { toast.error(linkErrMsg(e)); }
    finally { setBusy(false); }
  };

  const term = q.trim();
  return (
    <Shell title="Match patient" onClose={onClose} testid="patient-match-panel" wide closeTestid="pm-close">
      <div className="inline-flex rounded-[9px] p-1 mb-4" style={{ background: "#E9E9EC" }}>
        <button onClick={() => setTab("match")} data-testid="pm-tab-match" className="text-[12.5px] font-semibold rounded-[7px] px-3 py-1.5" style={{ background: tab === "match" ? "#fff" : "transparent", color: INK }}>Match existing</button>
        <button onClick={() => { setTab("add"); setDups(null); }} data-testid="pm-tab-add" className="text-[12.5px] font-semibold rounded-[7px] px-3 py-1.5" style={{ background: tab === "add" ? "#fff" : "transparent", color: INK }}>Add new patient</button>
      </div>

      {tab === "match" ? (
        <div className="flex flex-col gap-2">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: GREY }} />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, MRN or DOB" className="w-full text-[13px] pl-9 pr-3 py-2.5 rounded-[9px] outline-none bg-white" style={{ border: `1px solid ${HAIR}` }} data-testid="pm-search-input" />
            {searching ? <Loader2 size={15} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin" style={{ color: GREY }} /> : null}
          </div>

          {term.length < 2 ? (
            <div className="text-[12.5px] py-6 text-center" style={{ color: GREY }} data-testid="pm-search-hint">Type at least 2 characters to search this client's patients.</div>
          ) : searching && !rows.length ? (
            <div className="text-[12.5px] py-6 text-center" style={{ color: GREY }}>Searching…</div>
          ) : !rows.length ? (
            <div className="text-[12.5px] py-6 text-center" style={{ color: GREY }} data-testid="pm-no-results">{bound ? `No patients match “${term}”.` : "This client has no live chart connection or roster to search yet."}</div>
          ) : (
            <div className="flex flex-col gap-1.5 max-h-[340px] overflow-auto">
              {rows.map((r) => {
                const rowSrc = r.source || src;
                const isLinking = busy && linkingMrn === (r.mrn || r.full_name || "x");
                return (
                  <button key={r.mrn || r.full_name} type="button" onClick={() => pick(r)} disabled={busy} data-testid={`pm-result-${r.mrn}`}
                    className="text-left bg-white rounded-[11px] border p-3 flex items-center gap-3 disabled:opacity-60 hover:border-[#C9C9E8] transition-colors" style={{ borderColor: "#E9E9EC" }}>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13.5px] font-semibold truncate" style={{ color: INK }}>{r.full_name || [r.first_name, r.last_name].filter(Boolean).join(" ") || "Patient"}</div>
                      <div className="text-[12px] truncate" style={{ color: GREY }}>
                        {[r.mrn ? `MRN ${r.mrn}` : null, r.dob ? `DOB ${r.dob}` : null, r.gender || null, r.insurance?.payer_name || null].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                    <span className="text-[10.5px] font-bold uppercase tracking-[.04em] px-2 py-[3px] rounded-full flex-none" style={{ background: "#F1F1F4", color: "#6E6E6B" }}>{SRC_LABEL[rowSrc] || "Patient"}</span>
                    {isLinking ? <Loader2 size={14} className="animate-spin flex-none" style={{ color: INK }} /> : <span className="text-[12px] font-semibold flex-none" style={{ color: "#5856D6" }}>Link →</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-2.5">
          <div className="grid grid-cols-2 gap-2.5">
            <Field label="First name" k="first_name" f={f} setF={setF} testid="pm-add-first_name" />
            <Field label="Last name" k="last_name" f={f} setF={setF} testid="pm-add-last_name" />
            <Field label="Date of birth" k="dob" f={f} setF={setF} type="date" testid="pm-add-dob" />
            <div>
              <label className="text-[11px] font-bold uppercase" style={{ color: GREY, letterSpacing: ".05em" }}>Gender</label>
              <select value={f.gender} onChange={(e) => setF((s) => ({ ...s, gender: e.target.value }))} className="w-full text-[13px] px-3 py-2 rounded-[9px] outline-none bg-white mt-1" style={{ border: `1px solid ${HAIR}` }} data-testid="pm-add-gender"><option value="">—</option><option value="male">Male</option><option value="female">Female</option><option value="unknown">Unknown</option></select>
            </div>
            <Field label="Phone" k="phone" f={f} setF={setF} testid="pm-add-phone" />
            <Field label="Email" k="email" f={f} setF={setF} testid="pm-add-email" />
          </div>
          <Field label="Street address" k="street" f={f} setF={setF} testid="pm-add-street" />
          <div className="grid grid-cols-3 gap-2.5">
            <Field label="City" k="city" f={f} setF={setF} testid="pm-add-city" />
            <Field label="State" k="state" f={f} setF={setF} testid="pm-add-state" placeholder="NJ" />
            <Field label="ZIP" k="zip" f={f} setF={setF} testid="pm-add-zip" />
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            <div>
              <label className="text-[11px] font-bold uppercase" style={{ color: GREY, letterSpacing: ".05em" }}>Payer</label>
              <div className="mt-1"><PayerPicker clientId={clientId} value={f.payer_name ? `${f.payer_name}${f.payer_id ? ` · ${f.payer_id}` : ""}` : ""} onPick={(it) => setF((s) => ({ ...s, payer_name: it.payer_name, payer_id: it.payer_id || "" }))} /></div>
            </div>
            <Field label="Member ID" k="member_id" f={f} setF={setF} testid="pm-add-member_id" />
          </div>
          <Field label="MRN (blank = auto)" k="mrn" f={f} setF={setF} testid="pm-add-mrn" placeholder="leave blank for NTC-######" />

          <button onClick={() => save(false)} disabled={busy} className="mt-1 text-[13px] font-bold text-white rounded-[9px] px-4 py-2.5 disabled:opacity-40 inline-flex items-center justify-center gap-1.5" style={{ background: INK }} data-testid="pm-add-save">{busy ? <Loader2 size={13} className="animate-spin" /> : null}{busy ? "Adding…" : "Add & link patient"}</button>

          {dups ? (
            <div className="mt-1 rounded-[11px] border p-3 bg-white" style={{ borderColor: "#F0D8A8" }} data-testid="pm-duplicates">
              <div className="text-[12px] font-bold mb-2" style={{ color: "#9A5A00" }}>Possible duplicate{dups.length === 1 ? "" : "s"} — link one instead of creating a new record:</div>
              {(dups.length ? dups : [{ name: "existing patient", mrn: "", dob: "" }]).map((d, i) => (
                <div key={i} className="flex items-center gap-2 py-1.5 border-t first:border-0" style={{ borderColor: HAIR }}>
                  <div className="flex-1 text-[12.5px]" style={{ color: INK }}>{d.name || d.full_name || "Patient"}<span style={{ color: GREY }}>{d.mrn ? ` · ${d.mrn}` : ""}{d.dob ? ` · DOB ${d.dob}` : ""}</span></div>
                  {d.mrn ? <button onClick={() => linkDup(d.mrn)} disabled={busy} className="text-[12px] font-semibold rounded-[7px] px-3 py-1.5 border bg-white" style={{ borderColor: HAIR, color: INK }} data-testid={`pm-dup-link-${d.mrn}`}>Link this one</button> : null}
                </div>
              ))}
              <button onClick={() => save(true)} disabled={busy} className="mt-2 text-[12px] font-semibold" style={{ color: "#5856D6" }} data-testid="pm-add-force">Add as a new patient anyway</button>
            </div>
          ) : null}
        </div>
      )}
    </Shell>
  );
}
